/**
 * Drawer component - migrated from chariot-ui
 *
 * Migration reason: Close button positioning issue where X button overlaps drawer title
 *
 * Original: chariot-ui/Drawer
 * Migrated: 2025-10-20
 *
 * Changes from original:
 * - Fixed close button positioning from `-left-14` to `-left-20` to prevent overlap with drawer titles
 * - Added scrollResetKey prop for explicit scroll reset without remounting (2025-11-19)
 * - Maintained all other functionality and styling
 */

import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  popDrawerSlot,
  pushDrawerSlot,
  useDrawerEscape,
} from '@/hooks/hotkeys';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'parent' | 'child';
  escapeKey?: boolean;
  uuid?: string;
  scrollResetKey?: string; // When this changes, reset scroll to top
  initialWidth?: string; // Optional override for default width (e.g., '80%', '1200px')
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  className,
  style,
  variant = 'parent',
  uuid: _uuid = '', // Kept for API compatibility, unused after jiggle removal
  escapeKey = false,
  scrollResetKey,
  initialWidth,
}) => {
  const isParent = variant === 'parent';
  const defaultWidth = isParent ? '60%' : 'calc(61% - 60px)';

  const [width, setWidth] = useState<string>(initialWidth ?? defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const zIndex = isParent ? 50 : 51;

  // The element that had focus when this drawer opened (the "opener" — expected
  // to be the table scroll container the user `→`-entered from). Captured ONLY
  // if it sits OUTSIDE this drawer, so we never restore focus back into a
  // closing drawer. `wasOpenRef` makes restoration fire exactly once per close.
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  // Set the initial width of the drawer
  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (open) {
      if (variant === 'child' || !width) {
        const timer = setTimeout(
          () => setWidth(initialWidth ?? defaultWidth),
          0
        );
        return () => clearTimeout(timer);
      }
    }
  }, [open, initialWidth, defaultWidth, variant]);

  // Handle dragging to resize the drawer
  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const drawerElement = e.currentTarget.parentElement;
    if (!drawerElement) return;

    const currentWidth = drawerElement.getBoundingClientRect().width;
    const startX = e.pageX;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = startX - e.pageX;
      const newWidth = Math.max(
        300,
        Math.min(window.innerWidth * 0.8, currentWidth + deltaX)
      );
      setWidth(`${newWidth.toString()}px`);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const computedStyle = {
    ...style,
    width,
    zIndex,
  };

  // Reset scroll when scrollResetKey changes
  useEffect(() => {
    if (scrollResetKey !== undefined && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [scrollResetKey]);

  // Context-aware Escape: if focus is inside this drawer's tablist AND a table
  // armed a return target (via `→`-enter), return focus to the table and keep
  // the drawer open; otherwise close. A SINGLE handler decides return-vs-close
  // (react-hotkeys-hook v5 has no handler ordering guarantee). Gated by the
  // `escapeKey` prop so only the topmost drawer owns Esc-close.
  useDrawerEscape(onClose, scrollContainerRef, {
    enabled: escapeKey && open,
  });

  // WAI-ARIA dialog focus-restoration for the NORMAL close paths (✕ button,
  // backdrop click, Escape-that-closes, route/state change that unmounts the
  // drawer). The Esc-from-tabs return is handled separately by `useDrawerEscape`
  // + the `drawerFocusReturn` registry; this mirrors that for every other close
  // so the table's focus-gated arrow-key row navigation works again after the
  // drawer goes away (the browser strands focus on <body> when the focused
  // element unmounts).
  //
  // Restoration runs in the effect CLEANUP, never in an `open===false` effect
  // body: React fires an effect's cleanup before re-running it on a dep change,
  // so the single `return () => restoreFocus()` covers BOTH unmount (the path
  // every consumer takes — each renders this Drawer conditionally inside
  // <AnimatePresence>, see `Drawers` below and `GapDetailDrawer`, so closing it
  // unmounts the component) AND any `[open]` dep-change (covering the rare
  // consumer that keeps the Drawer mounted and merely toggles `open` to false).
  // A `wasOpenRef` once-guard makes a single close restore exactly once. We only
  // RECLAIM stranded focus (activeElement is null/body/non-element) — if the
  // user deliberately moved focus to another real element, we leave it alone.
  const captureOpener = () => {
    const active = document.activeElement;
    const container = scrollContainerRef.current;
    // Only capture an opener that is OUTSIDE this drawer; if focus is already
    // inside the drawer, leave opener null (never restore into a closing drawer).
    openerRef.current =
      active instanceof HTMLElement &&
      !(container && container.contains(active))
        ? active
        : null;
    wasOpenRef.current = true;

    // F5: claim a per-drawer focus-return slot on open (saving the outer
    // drawer's slot), so a CHILD drawer's later close pops only its OWN slot and
    // restores the PARENT's stored table return-target instead of wiping it.
    // Paired 1:1 with `popDrawerSlot()` in `restoreFocus` (same `wasOpenRef`
    // once-guard governs both).
    pushDrawerSlot();
  };

  const restoreFocus = () => {
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;

    const opener = openerRef.current;
    openerRef.current = null;

    // F5: pop THIS drawer's focus-return slot, restoring the OUTER (parent)
    // drawer's slot. Replaces the old unconditional `clearReturnTarget()`, which
    // wiped the single global slot on EVERY close — so closing a CHILD drawer
    // destroyed the PARENT's stored table return-target and the next Esc in the
    // parent closed instead of returning focus to the table. Paired 1:1 with
    // `pushDrawerSlot()` in `captureOpener` via the `wasOpenRef` once-guard, so
    // it also runs on unmount (where the old `if (!open)` effect body never did).
    popDrawerSlot();

    const active = document.activeElement;
    const stranded =
      active === null ||
      active === document.body ||
      !(active instanceof HTMLElement);

    if (opener && opener.isConnected && stranded) {
      opener.focus({ preventScroll: true });
    }
  };

  useEffect(() => {
    if (open) {
      captureOpener();
    }

    // Cleanup runs on unmount AND on every `[open]` dep-change. On unmount the
    // browser has stranded focus on <body>; reclaim it for the opener. On a
    // mounted true→false toggle this same cleanup fires before the re-run, so it
    // restores there too — no separate `open===false` branch needed.
    return () => {
      restoreFocus();
    };
  }, [open]);

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '20%' }}
      transition={{
        type: 'tween',
        duration: 0.2,
        ease: 'easeOut',
      }}
      className="pointer-events-none fixed inset-0"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={twMerge(
            'pointer-events-auto absolute inset-y-0 right-0',
            className,
            isDragging && 'select-none'
          )}
          style={computedStyle}
        >
          <div className="relative h-full">
            <div className="h-full">
              <div
                ref={scrollContainerRef}
                tabIndex={-1}
                className="bg-chariot-background-primary border-chariot-stroke h-full overflow-x-hidden overflow-y-scroll border-l-2 outline-none"
                data-testid="drawer-content"
              >
                <button
                  onClick={onClose}
                  className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary absolute top-6 -left-[52px] flex size-10 items-center justify-center rounded-full border-2 transition-colors"
                  data-testid="drawer-close-button"
                >
                  <span className="sr-only">Close panel</span>
                  <X className="text-chariot-text-primary size-5" />
                </button>

                <div
                  onMouseDown={startResizing}
                  className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary absolute top-1/2 -left-10 flex h-20 w-6 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border-2 transition-colors"
                  role="separator"
                  aria-label="Resize drawer"
                >
                  <div className="flex flex-row gap-1 p-2">
                    <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
                    <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
                  </div>
                </div>

                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

interface DrawersProps {
  children: React.ReactNode;
  disableBackdrop?: boolean;
}

export const Drawers: React.FC<DrawersProps> = ({
  children,
  disableBackdrop = false,
}) => {
  const childArray = React.Children.toArray(children);

  // Warn developers if attempting to open 3+ drawers
  if (childArray.length > 2) {
    console.warn(
      `[Drawers] Attempted to render ${childArray.length} drawers, but only 2 are supported. ` +
        `Oldest ${childArray.length - 2} drawer(s) will be hidden. ` +
        `Consider closing drawers before opening new ones.`
    );
  }

  // FIFO drawer limit: Only render last 2 drawers to prevent UI clutter
  // If 3+ drawers are queued, oldest drawers are dropped from rendering
  // To open a 3rd drawer, the application should close the oldest drawer first
  const lastTwoDrawers = childArray.slice(-2);
  const parentDrawer = lastTwoDrawers[0];
  const childDrawer = lastTwoDrawers[1];

  const hasParentAndChild =
    React.isValidElement<DrawerProps>(parentDrawer) &&
    React.isValidElement<DrawerProps>(childDrawer) &&
    parentDrawer.props.open &&
    childDrawer.props.open;

  return (
    <>
      {/* Parent Drawer */}
      <AnimatePresence>
        {React.isValidElement<DrawerProps>(parentDrawer) &&
          parentDrawer.props.open && (
            <Drawer
              {...parentDrawer.props}
              key="parent-drawer"
              variant="parent"
              escapeKey={!hasParentAndChild}
              style={{
                ...parentDrawer.props.style,
                width: hasParentAndChild ? 'calc(100% - 60px)' : '60%',
              }}
            />
          )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {hasParentAndChild && !disableBackdrop && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
              if (React.isValidElement<DrawerProps>(childDrawer)) {
                childDrawer.props.onClose();
              }
            }}
            className="bg-chariot-shade/50 pointer-events-auto fixed inset-0"
          />
        )}
      </AnimatePresence>

      {/* Child Drawer */}
      <AnimatePresence>
        {React.isValidElement<DrawerProps>(childDrawer) &&
          childDrawer.props.open && (
            <Drawer
              {...childDrawer.props}
              key={`child-drawer-${childDrawer.key ?? 'child'}`}
              variant="child"
              escapeKey={true}
              style={{
                width: 'calc(61% - 60px)',
                zIndex: 102,
              }}
            />
          )}
      </AnimatePresence>
    </>
  );
};
